import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode'; // Corrected import
import { useAuthStore } from '../store/authStore'; // Import your auth store

// Define role constants if not already shared
// You might want to import ROLES from '../config/routeConfig' if it's defined there
// export const ROLES = { BASIC: 'basic-subscriber', ... };

interface DecodedToken {
  'https://tradingdashboard.com/roles'?: string[];
}

// Add interface for stable state management
interface RolesState {
  roles: string[];
  isLoading: boolean;
  lastKnownRoles: string[];
  hasEverLoaded: boolean;
}

// Cache key for role persistence
const ROLES_CACHE_KEY = 'user_roles_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const useUserRoles = (): { roles: string[]; hasRole: (requiredRoles: string | string[]) => boolean; isLoading: boolean; hasEverLoaded: boolean } => {
  const { isLoading: isAuth0Loading, isAuthenticated } = useAuth0(); // Get both loading state and auth status
  const accessToken = useAuthStore(state => state.accessToken); // Get token from Zustand store

  // Helper function to get cached roles
  const getCachedRoles = (): string[] => {
    try {
      const cached = sessionStorage.getItem(ROLES_CACHE_KEY);
      if (cached) {
        const { roles, timestamp, ttl } = JSON.parse(cached);
        if (Date.now() - timestamp < ttl) {
          return roles;
        }
      }
    } catch (error) {
      console.warn('[useUserRoles] Failed to load cached roles:', error);
    }
    return [];
  };

  // Initialize with cached roles if available
  const [rolesState, setRolesState] = useState<RolesState>(() => {
    const cachedRoles = getCachedRoles();
    return {
      roles: [],
      isLoading: true,
      lastKnownRoles: cachedRoles,
      hasEverLoaded: cachedRoles.length > 0
    };
  });

  useEffect(() => {
    // If Auth0 is still loading, maintain current state
    if (isAuth0Loading) {
      setRolesState(prev => ({ ...prev, isLoading: true }));
      return;
    }

    // If user is not authenticated, clear roles but maintain stability
    if (!isAuthenticated) {
      setRolesState(prev => ({
        ...prev,
        roles: [],
        isLoading: false,
        hasEverLoaded: true
      }));
      console.log('[useUserRoles] User not authenticated, no roles to fetch.');
      return;
    }

    // User is authenticated, but do we have a token yet?
    if (!accessToken) {
      // Still waiting for the token, but don't reset hasEverLoaded if we have cached data
      setRolesState(prev => ({
        ...prev,
        roles: [],
        isLoading: true
      }));
      console.log('[useUserRoles] User authenticated but no access token yet. Still loading...');
      return;
    }

    // We have a token, decode it
    try {
      const decodedToken = jwtDecode<DecodedToken>(accessToken);
      const tokenRoles = decodedToken['https://tradingdashboard.com/roles'];
      
      if (Array.isArray(tokenRoles)) {
        // Cache the roles
        try {
          sessionStorage.setItem(ROLES_CACHE_KEY, JSON.stringify({
            roles: tokenRoles,
            timestamp: Date.now(),
            ttl: CACHE_TTL
          }));
        } catch (cacheError) {
          console.warn('[useUserRoles] Failed to cache roles:', cacheError);
        }
        
        setRolesState({
          roles: tokenRoles,
          isLoading: false,
          lastKnownRoles: tokenRoles,
          hasEverLoaded: true
        });
        console.log('[useUserRoles] Roles from decoded token:', tokenRoles);
      } else {
        setRolesState(prev => ({
          ...prev,
          roles: [],
          isLoading: false,
          hasEverLoaded: true
        }));
        // Only warn if user is authenticated and token is present but no roles found
        if (isAuthenticated && accessToken) {
          console.debug('[useUserRoles] Roles claim not found or not an array in token. This may be normal during token refresh.');
        }
      }
    } catch (error) {
      console.error('[useUserRoles] Error decoding access token:', error);
      setRolesState(prev => ({
        ...prev,
        roles: [],
        isLoading: false,
        hasEverLoaded: true
      }));
    }
  }, [isAuth0Loading, isAuthenticated, accessToken]); // Re-run effect when any of these change

  const hasRole = (requiredRoles: string | string[]): boolean => {
    // If still loading and no cached data, return false
    if (rolesState.isLoading && !rolesState.hasEverLoaded) return false; 

    if (!requiredRoles || (Array.isArray(requiredRoles) && requiredRoles.length === 0)) {
      return true; // No specific roles required, access granted
    }
    
    // Use current roles if available, otherwise fall back to last known roles
    const currentRoles = rolesState.roles.length > 0 ? rolesState.roles : rolesState.lastKnownRoles;
    const rolesToCheck = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    return rolesToCheck.some(role => currentRoles.includes(role));
  };

  // Return user roles state with stable data
  const stableRoles = rolesState.roles.length > 0 ? rolesState.roles : rolesState.lastKnownRoles;
  
  return { 
    roles: stableRoles, 
    hasRole, 
    isLoading: rolesState.isLoading, 
    hasEverLoaded: rolesState.hasEverLoaded 
  };
};

/*
Example usage (assuming you have a token and want to decode it):

interface DecodedToken {
  // Define the expected structure of your decoded token
  roles?: string[];
  exp?: number;
  // ... other claims
}

// ... inside your hook or function
try {
  const decoded = jwtDecode<DecodedToken>(token);
  // Now you can access claims like decoded.roles
} catch (error) {
  console.error("Failed to decode JWT:", error);
}
*/
